import type { ReactNode } from 'react'
import { Navigate, useLocation } from 'react-router'
import { useAuth } from '@/hooks/useAuth'
import { Button } from './Button'
import { ErrorNotice } from './ErrorNotice'
import { Spinner } from './Spinner'

export function FullPageLoader({ label = 'Loading' }: { label?: string }) {
  return (
    <div className="flex min-h-dvh items-center justify-center" role="status" aria-live="polite">
      <Spinner className="size-6 text-accent" />
      <span className="sr-only">{label}</span>
    </div>
  )
}

export function RequireAuth({ children }: { children: ReactNode }) {
  const { isLoading, isAuthenticated, error, refresh } = useAuth()
  const location = useLocation()

  if (isLoading) return <FullPageLoader label="Checking your session" />

  // A failed `me` that is not a 401 means Hermes itself could not be reached.
  // That is the one case where there is genuinely nothing to render yet.
  if (!isAuthenticated && error) {
    return (
      <div className="mx-auto flex min-h-dvh max-w-md flex-col items-center justify-center gap-4 px-4">
        <ErrorNotice error={error} />
        <Button variant="primary" onClick={() => void refresh()}>
          Retry
        </Button>
      </div>
    )
  }

  if (!isAuthenticated) {
    return (
      <Navigate to="/login" replace state={{ from: `${location.pathname}${location.search}` }} />
    )
  }

  return <>{children}</>
}

export function RequireAdmin({ children }: { children: ReactNode }) {
  const { isAdmin } = useAuth()

  if (!isAdmin) {
    return (
      <div className="rounded border border-line bg-surface px-6 py-12 text-center">
        <h1 className="text-lg font-semibold text-ink">Admins only</h1>
        <p className="mx-auto mt-2 max-w-sm text-sm text-muted">
          This area manages other people’s accounts, so it is limited to server admins.
        </p>
      </div>
    )
  }

  return <>{children}</>
}
